import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Award, TrendingUp, Zap, ArrowRight, CheckCircle, Star, Users, Target, Clock, Trophy, Quote, Sun, Moon, Sparkles, Rocket, GraduationCap, Brain, Lightbulb } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useRef, useState, useEffect } from 'react';

export default function Landing() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const statsRef = useRef(null);
  const isStatsInView = useInView(statsRef, { once: true });

  const educators = [
    {
      name: "Dr. Rajesh Kumar",
      role: "Physics Expert",
      credentials: "IIT Delhi, PhD",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop",
      experience: "15+ years",
      students: "10,000+",
      rating: "4.9",
      specialization: "Mechanics & Electromagnetism"
    },
    {
      name: "Prof. Anita Sharma",
      role: "Chemistry Specialist", 
      credentials: "IIT Bombay, M.Sc",
      image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop",
      experience: "12+ years",
      students: "8,500+",
      rating: "4.8",
      specialization: "Organic & Inorganic Chemistry"
    },
    {
      name: "Dr. Vikram Singh",
      role: "Mathematics Guru",
      credentials: "IIT Kanpur, PhD",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop",
      experience: "18+ years",
      students: "12,000+",
      rating: "5.0",
      specialization: "Calculus & Algebra"
    }
  ];

  const features = [
    {
      icon: <Target className="w-10 h-10" />,
      title: "JEE-Focused Content",
      description: "Questions designed specifically for JEE Main & Advanced patterns",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Clock className="w-10 h-10" />,
      title: "Timed Mock Tests",
      description: "Real exam environment with timer to improve speed and accuracy",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: "Instant Analytics",
      description: "Get detailed performance reports after every test attempt",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: <GraduationCap className="w-10 h-10" />,
      title: "Expert Guidance",
      description: "Learn from IIT faculty with decades of teaching experience",
      color: "from-green-500 to-emerald-500"
    }
  ];

  const testimonials = [
    {
      name: "Arjun Mehta",
      rank: "AIR 247, JEE Advanced",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
      text: "The test series helped me identify my weak areas and improve systematically. The quality of questions is outstanding!",
      rating: 5
    },
    {
      name: "Priya Desai",
      rank: "AIR 412, JEE Advanced",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
      text: "Amazing platform! The detailed analytics after each test helped me track my progress and stay motivated.",
      rating: 5
    },
    {
      name: "Rohan Kapoor",
      rank: "99.8 Percentile, JEE Main",
      image: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop",
      text: "Best test series for JEE preparation. The difficulty level matches the actual exam perfectly.",
      rating: 5
    }
  ];

  const stats = [
    { value: 50000, label: "Students Enrolled", suffix: "+" },
    { value: 1000, label: "Practice Tests", suffix: "+" },
    { value: 98, label: "Success Rate", suffix: "%" },
    { value: 4.9, label: "Student Rating", suffix: "/5" }
  ];

  const [counters, setCounters] = useState(stats.map(() => 0));

  useEffect(() => {
    if (isStatsInView) {
      stats.forEach((stat, index) => {
        const duration = 2000;
        const steps = 60;
        const increment = stat.value / steps;
        let current = 0;

        const timer = setInterval(() => {
          current += increment;
          if (current >= stat.value) {
            current = stat.value;
            clearInterval(timer);
          }
          setCounters(prev => {
            const newCounters = [...prev];
            newCounters[index] = current;
            return newCounters;
          });
        }, duration / steps);
      });
    }
  }, [isStatsInView]);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-hidden">
      {/* Navbar */}
      <nav className="border-b border-border backdrop-blur-md bg-background/80 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <motion.div
            className="flex items-center gap-2 cursor-pointer"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
          >
            <img 
              src="/pinaka-logo.png" 
              alt="Pinaka Education"
              className="h-12 w-auto"
            />
          </motion.div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              className="rounded-full"
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? (
                <Sun className="w-5 h-5 text-warning" />
              ) : (
                <Moon className="w-5 h-5 text-primary" />
              )}
            </Button>
            {user ? (
              <>
                <Button 
                  onClick={() => navigate('/dashboard')} 
                  data-testid="nav-dashboard-btn"
                  className="bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg"
                >
                  Dashboard
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => { logout(); navigate('/'); }}
                  data-testid="nav-logout-btn"
                >
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')}
                  data-testid="nav-login-btn"
                  className="text-foreground hover:text-accent"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => navigate('/register')}
                  data-testid="nav-register-btn"
                  className="bg-primary hover:bg-primary/90 text-white font-semibold shadow-lg"
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Announcement Banner */}
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 text-white py-3">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 text-sm font-semibold">
            <Sparkles className="w-5 h-5 animate-pulse" />
            <span>Special Offer: Get 20% OFF on all test series! Use code: JEE2026</span>
            <Sparkles className="w-5 h-5 animate-pulse" />
          </div>
        </div>
      </div>

      {/* Hero Section with Animated Background */}
      <section className="relative overflow-hidden py-16 md:py-24 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div 
            className="absolute top-20 left-10 w-72 h-72 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 blur-3xl"
            animate={{
              y: [0, 30, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
          <motion.div 
            className="absolute bottom-20 right-10 w-96 h-96 rounded-full bg-gradient-to-br from-accent/20 to-primary/20 blur-3xl"
            animate={{
              y: [0, -40, 0],
              scale: [1, 1.15, 1],
            }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1
            }}
          />
          <motion.div 
            className="absolute top-1/2 left-1/2 w-80 h-80 rounded-full bg-gradient-to-br from-secondary/15 to-accent/15 blur-3xl"
            animate={{
              y: [0, 25, 0],
              x: [0, 25, 0],
              scale: [1, 1.08, 1],
            }}
            transition={{
              duration: 7,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 0.5
            }}
          />
        </div>

        {/* Floating Icons */}
        <motion.div 
          className="absolute top-32 right-1/4 text-primary/30"
          animate={{
            y: [0, -20, 0],
            rotate: [0, 10, 0]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <Brain className="w-16 h-16" />
        </motion.div>
        <motion.div 
          className="absolute bottom-32 left-1/4 text-accent/30"
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 0]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        >
          <Lightbulb className="w-20 h-20" />
        </motion.div>
        <motion.div 
          className="absolute top-1/2 right-20 text-secondary/30"
          animate={{
            y: [0, -15, 0],
            rotate: [0, 15, 0]
          }}
          transition={{
            duration: 4.5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 0.5
          }}
        >
          <Rocket className="w-14 h-14" />
        </motion.div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-primary/10 to-accent/10 border-2 border-primary/20 rounded-full mb-8"
              >
                <Trophy className="w-5 h-5 text-primary" />
                <span className="text-sm font-bold text-primary">PINAKA • 10th, 12th, JEE & NEET Test Series</span>
              </motion.div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-outfit font-bold mb-6 leading-tight">
                Aim. Achieve.{' '}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent animate-gradient">
                    Succeed.
                  </span>
                  <motion.div
                    className="absolute -bottom-2 left-0 right-0 h-3 bg-gradient-to-r from-primary/30 to-accent/30 blur-sm"
                    animate={{
                      scaleX: [0.8, 1, 0.8],
                      opacity: [0.5, 0.8, 0.5]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                </span>
                <br />
                Your Success Starts Here
              </h1>
              
              <p className="text-xl md:text-2xl text-muted-foreground mb-10 leading-relaxed max-w-3xl mx-auto">
                Comprehensive test series for <span className="text-primary font-semibold">JEE Main, JEE Advanced & NEET</span>. Practice with expert-curated questions and detailed analytics.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Button 
                  size="lg" 
                  onClick={() => {
                    if (user) {
                      navigate('/free-trial');
                    } else {
                      navigate('/register', { state: { returnTo: '/free-trial' } });
                    }
                  }}
                  data-testid="hero-cta-btn"
                  className="bg-gradient-to-r from-primary to-secondary hover:opacity-90 text-white font-bold shadow-xl group text-lg px-10 py-7"
                >
                  <Rocket className="mr-2 w-6 h-6" />
                  Start Free Trial
                  <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => navigate('/test-series')}
                  data-testid="hero-browse-btn"
                  className="border-2 border-primary text-primary hover:bg-primary hover:text-white text-lg px-10 py-7 font-bold shadow-lg"
                >
                  <BookOpen className="mr-2 w-5 h-5" />
                  Browse 1000+ Tests
                </Button>
              </div>

              {/* Quick Stats Inline */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-wrap justify-center gap-6 md:gap-10"
              >
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">50K+</div>
                    <div className="text-sm text-muted-foreground">Students</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">98%</div>
                    <div className="text-sm text-muted-foreground">Success Rate</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
                    <Star className="w-6 h-6 text-white fill-white" />
                  </div>
                  <div className="text-left">
                    <div className="text-2xl font-bold">4.9/5</div>
                    <div className="text-sm text-muted-foreground">Rating</div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Educators Section - COMMENTED OUT
      <section className="py-20 bg-gradient-to-br from-muted/50 to-background">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-full mb-4">
              <GraduationCap className="w-5 h-5 text-primary" />
              <span className="text-sm font-bold text-primary">World-Class Faculty</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-outfit font-bold mb-4">
              Learn from <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">IIT Experts</span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Get guidance from India's top educators with proven track records
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {educators.map((educator, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="group cursor-pointer"
              >
                <div className="bg-card border-2 border-border hover:border-primary/50 rounded-3xl overflow-hidden transition-all shadow-xl hover:shadow-2xl">
                  <div className="relative h-80 overflow-hidden">
                    <img 
                      src={educator.image}
                      alt={educator.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
                    <div className="absolute top-4 right-4 bg-gradient-to-r from-primary to-secondary text-white px-4 py-2 rounded-full font-bold shadow-lg flex items-center gap-1">
                      <Star className="w-4 h-4 fill-white" />
                      {educator.rating}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-2xl font-outfit font-bold mb-2">{educator.name}</h3>
                    <div className="text-primary font-bold text-lg mb-2">{educator.role}</div>
                    <div className="text-sm text-muted-foreground mb-3">{educator.credentials}</div>
                    <div className="text-sm font-semibold text-accent mb-4">{educator.specialization}</div>
                    <div className="flex gap-4 pt-4 border-t-2 border-border">
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-bold text-primary">{educator.students}</div>
                          <div className="text-xs text-muted-foreground">Students</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-accent/20 to-primary/20 flex items-center justify-center">
                          <Award className="w-5 h-5 text-accent" />
                        </div>
                        <div>
                          <div className="font-bold text-accent">{educator.experience}</div>
                          <div className="text-xs text-muted-foreground">Experience</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      */}

      {/* Colorful Promo Banner */}
      <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIgb3BhY2l0eT0iMC4xIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-outfit font-bold text-white mb-4">
              Limited Time Offer!
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Get access to ALL test series at 50% OFF. Perfect your JEE prep with unlimited practice tests.
            </p>
            <Button 
              size="lg"
              onClick={() => navigate('/register')}
              className="bg-white text-red-600 hover:bg-gray-100 font-bold text-lg px-12 py-6 shadow-2xl"
            >
              Claim Your Offer Now
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Stats Counter Section */}
      <section ref={statsRef} className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={isStatsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1 }}
                className="text-center p-6 bg-card rounded-2xl border-2 border-border shadow-lg"
              >
                <div className="text-4xl md:text-5xl font-bold font-mono bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent mb-2">
                  {stat.suffix === "%" || stat.suffix === "/5" 
                    ? counters[index].toFixed(1) 
                    : Math.floor(counters[index]).toLocaleString()}
                  {stat.suffix}
                </div>
                <div className="text-muted-foreground font-semibold">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-outfit font-bold mb-4">
              Everything You Need to Succeed
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Comprehensive features designed for JEE success
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="relative group"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`}></div>
                <div className="relative bg-card border-2 border-border rounded-2xl p-8 h-full hover:border-primary/50 transition-all shadow-lg hover:shadow-xl">
                  <div className={`inline-flex p-4 rounded-xl bg-gradient-to-br ${feature.color} text-white mb-6 shadow-lg`}>
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-outfit font-bold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-outfit font-bold mb-4">
              Success Stories from Our Students
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              See how we helped thousands crack JEE
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                whileHover={{ y: -8 }}
                className="relative"
              >
                <div className="bg-card border-2 border-border rounded-2xl p-8 h-full hover:border-primary/50 transition-all shadow-lg hover:shadow-xl">
                  <Quote className="w-10 h-10 text-accent mb-4 opacity-50" />
                  <p className="text-muted-foreground mb-6 leading-relaxed italic">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-warning text-warning" />
                    ))}
                  </div>
                  <div className="flex items-center gap-4 pt-4 border-t-2 border-border">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-16 h-16 rounded-full object-cover border-4 border-primary shadow-lg"
                    />
                    <div>
                      <div className="font-bold text-lg">{testimonial.name}</div>
                      <div className="text-sm text-success font-semibold">{testimonial.rank}</div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden bg-gradient-to-br from-purple-600 via-blue-600 to-cyan-600">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIgb3BhY2l0eT0iMC4xIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-outfit font-bold text-white mb-6">
              Start Your JEE Journey Today
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join 50,000+ students who trust us for their JEE preparation
            </p>
            <Button 
              size="lg"
              onClick={() => navigate(user ? '/test-series' : '/register')}
              className="bg-white text-purple-600 hover:bg-gray-100 font-bold text-lg px-12 py-6 shadow-2xl"
            >
              Get Started for Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/b913f950-9aae-49dc-aba5-d378a13b23f2/images/cfb7f6d255e29e51a76f73861f035a7bbb40bada25de0eb5061520d6d7d4dab1.png" 
                  alt="Pinaka Logo"
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-muted-foreground text-sm">
                Your trusted partner for JEE preparation with comprehensive test series and analytics.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/test-series" className="hover:text-primary transition-colors">Test Series</a></li>
                <li><a href="/login" className="hover:text-primary transition-colors">Login</a></li>
                <li><a href="/register" className="hover:text-primary transition-colors">Sign Up</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/terms" className="hover:text-primary transition-colors">Terms & Conditions</a></li>
                <li><a href="/privacy" className="hover:text-primary transition-colors">Privacy Policy</a></li>
                <li><a href="/refund-policy" className="hover:text-primary transition-colors">Refund Policy</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="mailto:support@pinakaclasses.com" className="hover:text-primary transition-colors">support@pinakaclasses.com</a></li>
                <li><a href="https://wa.me/918601375676" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">+91 86013 75676</a></li>
              </ul>
            </div>
          </div>
          <div className="text-center pt-8 border-t border-border text-muted-foreground text-sm">
            © {new Date().getFullYear()} Pinaka Education. All rights reserved.
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  );
}
